import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, RefreshCw, Send, CheckCircle2, Upload, Archive, Loader2, Edit3, Trash2, Save, X, List } from 'lucide-react';

export default function AdminAutomation() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ document_type: '', service_family: '', state: '', language: '', portal_name: '', portal_url: '' });
  const [creating, setCreating] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [fieldMappings, setFieldMappings] = useState([]);
  const [newField, setNewField] = useState({ question_id: '', portal_field: '', selector: '', required: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadProfiles(); }, []);

  const loadProfiles = async () => {
    try {
      const { data } = await api.get('/admin/automation-profiles');
      setProfiles(data.profiles || []);
    } catch (err) {
      toast.error('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/admin/automation-profiles', form);
      toast.success('Profile created');
      setShowForm(false);
      setForm({ document_type: '', service_family: '', state: '', language: '', portal_name: '', portal_url: '' });
      loadProfiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create profile');
    } finally {
      setCreating(false);
    }
  };

  const doAction = async (id, action) => {
    try {
      await api.post(`/admin/automation-profiles/${id}/${action}`);
      toast.success(`Profile ${action}ed`);
      loadProfiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const openFieldEditor = async (profile) => {
    try {
      const { data } = await api.get(`/admin/automation-profiles/${profile.id}`);
      const fm = data.field_mapping || {};
      const sm = data.selector_mapping || {};
      const rows = Object.keys(fm).length > 0
        ? Object.entries(fm).map(([qid, cfg]) => ({
            question_id: qid,
            portal_field: cfg.portal_field || '',
            selector: sm[qid] || cfg.selector || '',
            required: cfg.required !== false,
            type: cfg.type || 'text',
            options: Array.isArray(cfg.options) ? cfg.options.join(', ') : '',
          }))
        : (data.required_customer_fields || []).map(f => ({
            question_id: f, portal_field: '', selector: '', required: true, type: 'text', options: '',
          }));
      setFieldMappings(rows.length > 0 ? rows : [{ question_id: '', portal_field: '', selector: '', required: true, type: 'text', options: '' }]);
      setEditingProfile(data);
    } catch (err) {
      toast.error('Failed to load profile details');
    }
  };

  const addFieldRow = () => {
    setFieldMappings([...fieldMappings, { question_id: '', portal_field: '', selector: '', required: true, type: 'text', options: '' }]);
  };

  const removeFieldRow = (idx) => {
    if (fieldMappings.length <= 1) return;
    setFieldMappings(fieldMappings.filter((_, i) => i !== idx));
  };

  const updateFieldRow = (idx, key, value) => {
    const updated = [...fieldMappings];
    updated[idx] = { ...updated[idx], [key]: value };
    setFieldMappings(updated);
  };

  const saveFieldMapping = async () => {
    setSaving(true);
    try {
      const fm = {};
      const sm = {};
      fieldMappings.forEach(row => {
        if (!row.question_id) return;
        fm[row.question_id] = {
          portal_field: row.portal_field,
          required: row.required,
          type: row.type,
          options: row.options ? row.options.split(',').map(s => s.trim()).filter(Boolean) : [],
        };
        sm[row.question_id] = row.selector;
      });
      await api.patch(`/admin/automation-profiles/${editingProfile.id}`, {
        field_mapping: fm,
        selector_mapping: sm,
        required_customer_fields: fieldMappings.filter(r => r.question_id).map(r => r.question_id),
      });
      toast.success('Field mappings saved');
      setEditingProfile(null);
      loadProfiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save field mappings');
    } finally {
      setSaving(false);
    }
  };

  const statusColor = (s) => ({
    draft: 'bg-gray-500/20 text-gray-400',
    agent_review: 'bg-yellow-500/20 text-yellow-400',
    admin_review: 'bg-blue-500/20 text-blue-400',
    published: 'bg-green-500/20 text-green-400',
    archived: 'bg-red-500/20 text-red-400',
  }[s] || 'bg-gray-500/20 text-gray-400');

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#00a884]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Automation Profiles</h2>
          <p className="text-[#8696a0] text-sm mt-1">Create, edit field mappings, and publish portal automation profiles</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadProfiles} className="p-2 text-[#8696a0] hover:text-white transition"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={() => setShowForm(true)} className="flex items-center gap-1 px-3 py-2 bg-[#00a884] text-white text-sm rounded-lg hover:bg-[#06cf9c]">
            <Plus className="w-4 h-4" /> New Profile
          </button>
        </div>
      </div>

      <div className="grid gap-4">
        {profiles.length === 0 ? (
          <div className="text-center py-12 text-[#8696a0]">No automation profiles yet. Create one to get started.</div>
        ) : profiles.map(p => {
          const fieldCount = p.field_mapping ? Object.keys(p.field_mapping).length : 0;
          const stepCount = Array.isArray(p.playwright_steps) ? p.playwright_steps.length : 0;
          return (
          <div key={p.id} className="bg-[#111b21] rounded-xl border border-[#2a3942] p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-white font-semibold">{p.document_type || p.service_family || 'Untitled'}</h3>
                <p className="text-[#8696a0] text-xs mt-0.5">
                  v{p.version} · {p.service_family || '—'} · {p.state || 'All states'} · {p.language || 'en'}
                  {fieldCount > 0 && ` · ${fieldCount} fields`}
                  {stepCount > 0 && ` · ${stepCount} steps`}
                </p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${statusColor(p.status)}`}>{p.status}</span>
            </div>
            {p.portal_name && <p className="text-[#8696a0] text-xs mb-3">{p.portal_name}{p.portal_url ? ` — ${p.portal_url}` : ''}</p>}
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => openFieldEditor(p)}
                className="flex items-center gap-1 px-3 py-1.5 bg-[#202c33] text-white text-xs rounded-lg hover:bg-[#2a3942]">
                <List className="w-3 h-3" /> {fieldCount > 0 ? `${fieldCount} Fields` : 'Edit Fields'}
              </button>
              {p.status === 'draft' && <button onClick={() => doAction(p.id, 'send-agent-review')} className="flex items-center gap-1 px-3 py-1.5 bg-yellow-600 text-white text-xs rounded-lg hover:bg-yellow-700"><Send className="w-3 h-3" /> Send to Agent</button>}
              {p.status === 'agent_review' && <button onClick={() => doAction(p.id, 'approve')} className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700"><CheckCircle2 className="w-3 h-3" /> Approve</button>}
              {p.status === 'admin_review' && <button onClick={() => doAction(p.id, 'publish')} className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700"><Upload className="w-3 h-3" /> Publish</button>}
              {p.status !== 'archived' && <button onClick={() => doAction(p.id, 'archive')} className="flex items-center gap-1 px-3 py-1.5 bg-red-600/50 text-white text-xs rounded-lg hover:bg-red-600"><Archive className="w-3 h-3" /> Archive</button>}
            </div>
          </div>
        )})}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowForm(false)}>
          <form onSubmit={handleCreate} className="bg-[#111b21] rounded-xl border border-[#2a3942] p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-semibold text-lg mb-4">New Automation Profile</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              {['document_type', 'service_family', 'state', 'language', 'portal_name', 'portal_url'].map(f => (
                <div key={f}>
                  <label className="text-[#8696a0] text-xs block mb-1 capitalize">{f.replace(/_/g, ' ')}</label>
                  <input value={form[f]} onChange={e => setForm({...form, [f]: e.target.value})}
                    className="w-full bg-[#202c33] text-white border border-[#374045] rounded-lg px-3 py-2 text-sm" />
                </div>
              ))}
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-[#8696a0] hover:text-white">Cancel</button>
              <button type="submit" disabled={creating} className="px-4 py-2 bg-[#00a884] text-white text-sm rounded-lg hover:bg-[#06cf9c] disabled:opacity-50">
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      {editingProfile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditingProfile(null)}>
          <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-6 w-full max-w-3xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold text-lg">
                Field Mappings — {editingProfile.document_type || editingProfile.service_family || 'Untitled'}
              </h3>
              <button onClick={() => setEditingProfile(null)} className="text-[#8696a0] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-[#8696a0] text-xs mb-4">
              Map each question ID to a portal field name and CSS selector. Rows with empty question_id are ignored.
            </p>

            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[#8696a0] text-xs uppercase border-b border-[#2a3942]">
                    <th className="px-3 py-2 text-left">Question ID</th>
                    <th className="px-3 py-2 text-left">Portal Field</th>
                    <th className="px-3 py-2 text-left">CSS Selector</th>
                    <th className="px-3 py-2 text-center">Req.</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-center">Del</th>
                  </tr>
                </thead>
                <tbody>
                  {fieldMappings.map((row, i) => (
                    <tr key={i} className="border-b border-[#2a3942]">
                      <td className="px-3 py-2">
                        <input value={row.question_id} onChange={e => updateFieldRow(i, 'question_id', e.target.value)}
                          className="w-full bg-[#202c33] text-white border border-[#374045] rounded px-2 py-1 text-xs font-mono" placeholder="e.g. full_name" />
                      </td>
                      <td className="px-3 py-2">
                        <input value={row.portal_field} onChange={e => updateFieldRow(i, 'portal_field', e.target.value)}
                          className="w-full bg-[#202c33] text-white border border-[#374045] rounded px-2 py-1 text-xs" placeholder="Portal field name" />
                      </td>
                      <td className="px-3 py-2">
                        <input value={row.selector} onChange={e => updateFieldRow(i, 'selector', e.target.value)}
                          className="w-full bg-[#202c33] text-white border border-[#374045] rounded px-2 py-1 text-xs font-mono" placeholder="#css_selector" />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input type="checkbox" checked={row.required} onChange={e => updateFieldRow(i, 'required', e.target.checked)}
                          className="accent-[#00a884]" />
                      </td>
                      <td className="px-3 py-2">
                        <select value={row.type} onChange={e => updateFieldRow(i, 'type', e.target.value)}
                          className="bg-[#202c33] text-white border border-[#374045] rounded px-2 py-1 text-xs">
                          <option value="text">text</option>
                          <option value="select">select</option>
                          <option value="date">date</option>
                          <option value="email">email</option>
                          <option value="mobile">mobile</option>
                          <option value="file_upload">file</option>
                        </select>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button onClick={() => removeFieldRow(i)} className="text-red-400 hover:text-red-300 p-1">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button onClick={addFieldRow} className="flex items-center gap-1 px-3 py-1.5 text-xs text-[#00a884] hover:text-white transition mb-4">
              <Plus className="w-3 h-3" /> Add Field Mapping
            </button>

            <div className="flex gap-2 justify-end border-t border-[#2a3942] pt-4">
              <button onClick={() => setEditingProfile(null)} className="px-4 py-2 text-sm text-[#8696a0] hover:text-white">Cancel</button>
              <button onClick={saveFieldMapping} disabled={saving}
                className="flex items-center gap-1 px-4 py-2 bg-[#00a884] text-white text-sm rounded-lg hover:bg-[#06cf9c] disabled:opacity-50">
                <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Field Mappings'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
